import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';

import css from './PipAwareVideo.module.css';

const isInPip = video =>
  (typeof document !== 'undefined' && document.pictureInPictureElement === video) ||
  video.webkitPresentationMode === 'picture-in-picture';

/**
 * <video> that covers its own inline slot while it plays in Picture-in-Picture.
 *
 * iOS Safari leaves the inline <video> blank/black while the video plays in the
 * floating PiP window (desktop Chrome paints a placeholder, Safari doesn't), so
 * we render the poster with a "playing in PiP" message over it instead. Tapping
 * the cover brings the video back inline.
 *
 * Listens to both the standard PiP events and Safari's webkitpresentationmodechanged,
 * since which one fires depends on the iOS version.
 *
 * @param {Object} props
 * @param {string?} props.className - Wrapper class (layout: margins, max-width)
 * @param {string?} props.videoClassName - Class for the <video> element itself
 * @param {string?} props.poster - Poster URL, also used as the cover background
 * @param {ReactNode?} props.children - e.g. <source> elements
 * All other props are passed to the <video> element.
 */
const PipAwareVideo = props => {
  const { className, videoClassName, poster, children, ...videoProps } = props;
  const intl = useIntl();
  const videoRef = useRef(null);
  const [inPip, setInPip] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const sync = () => setInPip(isInPip(video));
    const events = [
      'enterpictureinpicture',
      'leavepictureinpicture',
      'webkitpresentationmodechanged',
    ];
    events.forEach(e => video.addEventListener(e, sync));
    return () => events.forEach(e => video.removeEventListener(e, sync));
  }, []);

  const handleReturnInline = () => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof document !== 'undefined' && document.pictureInPictureElement === video) {
      document.exitPictureInPicture().catch(() => {});
    } else if (typeof video.webkitSetPresentationMode === 'function') {
      video.webkitSetPresentationMode('inline');
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      <video ref={videoRef} className={videoClassName} poster={poster} {...videoProps}>
        {children}
      </video>

      {inPip ? (
        <button
          type="button"
          className={css.pipCover}
          style={poster ? { backgroundImage: `url("${poster}")` } : undefined}
          onClick={handleReturnInline}
        >
          <span className={css.pipMessage}>
            <span className={css.pipTitle}>
              {intl.formatMessage({ id: 'PipAwareVideo.playingInPip' })}
            </span>
            <span className={css.pipHint}>
              {intl.formatMessage({ id: 'PipAwareVideo.returnInline' })}
            </span>
          </span>
        </button>
      ) : null}
    </div>
  );
};

export default PipAwareVideo;
